import React from 'react';

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: string | React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  image?: string;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  image,
  className = '',
}) => {
  return (
    <div
      className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}
    >
      {image && (
        <img
          src={image}
          alt={title}
          className="w-48 h-48 mb-6 opacity-50 dark:opacity-30"
        />
      )}
      
      {icon && (
        <div className="mb-4 text-6xl text-luna-text-light/40 dark:text-luna-text-dark/40">
          {typeof icon === 'string' ? (
            <span className="material-symbols-outlined text-6xl">{icon}</span>
          ) : (
            icon
          )}
        </div>
      )}

      <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark mb-2">
        {title}
      </h3>

      {description && (
        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-md mb-6">
          {description}
        </p>
      )}

      {action && (
        <button
          onClick={action.onClick}
          className="px-6 py-3 bg-luna-secondary text-luna-primary font-bold rounded-lg hover:opacity-90 transition-opacity"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

// Pre-built empty states for common scenarios
export const EmptyCases: React.FC<{ onNewCase: () => void }> = ({ onNewCase }) => (
  <EmptyState
    title="No cases yet"
    description="Create your first case to get started with legal document management and AI-powered analysis."
    icon="folder_off"
    action={{
      label: 'Create Your First Case',
      onClick: onNewCase,
    }}
  />
);

export const EmptyDocuments: React.FC<{ onUpload?: () => void }> = ({ onUpload }) => (
  <EmptyState
    title="No documents"
    description="Upload documents to analyze them with AI and get legal insights."
    icon="description"
    action={
      onUpload
        ? {
            label: 'Upload Document',
            onClick: onUpload,
          }
        : undefined
    }
  />
);

export const EmptyMessages: React.FC = () => (
  <EmptyState
    title="No messages yet"
    description="Start a conversation with the AI assistant to get legal advice and insights."
    icon="chat_bubble_outline"
  />
);

export const EmptySearch: React.FC<{ query: string }> = ({ query }) => (
  <EmptyState
    title={`No results for "${query}"`}
    description="Try adjusting your search terms or filters to find what you're looking for."
    icon="search_off"
  />
);

export default EmptyState;

